<template>
  <div class="member-register affiliation-info">
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">勤務先／学校名</label>
        <q-input
          ref="workSchoolElem"
          v-model="inputData.work_school_name"
          outlined
          dense
          maxlength="150"
          :rules="[
            (val) => isEmpty(val) || $t('validation.required'),
            (val) => !isHalfKana(val) || $t('validation.half_kana'),
          ]"
          lazy-rules
          autofocus
          class="input-area"
          hint="例）ATTO株式会社"
          @keydown.enter.prevent
        />
      </div>
    </div>
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">所属／学部名</label>
        <q-input
          v-model="inputData.affiliation_department_name"
          outlined
          dense
          maxlength="200"
          :rules="[
            (val) => isEmpty(val) || $t('validation.required'),
            (val) => !isHalfKana(val) || $t('validation.half_kana'),
          ]"
          lazy-rules
          class="input-area"
          hint="例）営業部"
          @keydown.enter.prevent
        />
      </div>
    </div>
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">郵便番号</label>
        <div class="row">
          <div class="col" style="width: 220px; flex: none">
            <q-input
              v-model="inputData.postal_cd"
              outlined
              dense
              type="tel"
              maxlength="7"
              hint="例）1030022（ハイフン不要）"
              :rules="[(val) => isEmpty(val) || $t('validation.required')]"
              lazy-rules
              mask="#######"
              class="input-area"
              @keyup="searchAddress"
              @keydown.enter.prevent
              @keypress.enter.prevent
            />
          </div>
          <div class="col column justify-center" style="width: calc(100% - 220px)">
            <span class="affiliation-info-form-note">郵便番号入力で住所自動表示</span>
          </div>
        </div>
      </div>
    </div>
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">都道府県</label>
        <PrefecturesSelects
          class="q-field--with-bottom input-area"
          :value="inputData.todohuken_name"
          @select-value="inputData.todohuken_name = $event"
        />
      </div>
    </div>
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">市区町村以下</label>
        <q-input
          ref="streetAddressElem"
          v-model="inputData.street_address"
          outlined
          dense
          maxlength="200"
          :rules="[
            (val) => isEmpty(val) || $t('validation.required'),
            (val) => !isHalfKana(val) || $t('validation.half_kana'),
          ]"
          lazy-rules
          class="input-area"
          hint="例）中央区日本橋室町4丁目5-1 さくら室町ビル5階"
          @keydown.enter.prevent
        />
      </div>
    </div>
    <div class="row q-gutter-sm">
      <div class="col">
        <label class="requier">電話番号</label>
        <q-input
          v-model="inputData.phone_number"
          outlined
          dense
          type="tel"
          maxlength="20"
          hint="例）0362626467（ハイフン不要）"
          :rules="[(val) => isEmpty(val) || $t('validation.required')]"
          mask="####################"
          class="input-area tel-area"
          @keydown.enter.prevent
          @keypress.enter.prevent
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { Core as YubinBangoCore } from 'yubinbango-core2';
import PrefecturesSelects from '../../selects/PrefecturesSelects.vue';
import { isEmpty, isHalfKana } from '../../../common/utils/validateUtils';

interface Props {
  memberInfo: {
    work_school_name: string;
    affiliation_department_name: string;
    postal_cd: string;
    todohuken_name: string;
    street_address: string;
    phone_number: string;
  };
}

const props = defineProps<Props>();
const emit = defineEmits(['update:inputData']);

// 入力値
const inputData = computed({
  get: () => props.memberInfo,
  set: (value) => {
    emit('update:inputData', value);
  },
});

// 市区町村Focus用
const streetAddressElem = ref<HTMLImageElement>();

/**
 * 住所取得
 * @param evt キーボードイベント
 */
const searchAddress = (evt: KeyboardEvent) => {
  const { postal_cd } = inputData.value;
  // 郵便番号が7桁だった場合
  if (postal_cd.length === 7) {
    // 住所情報取得
    const yubinBangoCore = new YubinBangoCore();
    yubinBangoCore.getAddr(postal_cd, (value: { [key: string]: string }) => {
      inputData.value.todohuken_name = value.region; // 都道府県
      inputData.value.street_address = value.locality; // 市区町村
      inputData.value.street_address += value.street; // 町域
      // 数値入力された場合のみ市区町村にFocus
      const keyNum = parseFloat(evt.key);
      if (!Number.isNaN(keyNum)) {
        streetAddressElem.value?.focus();
      }
    });
  }
};

/**
 * 初期表示処理
 */
// topにスクロール
onMounted(() => {
  window.scrollTo({
    top: 0,
  });
});
</script>
<style scoped>
.affiliation-info-form-note {
  font-size: 0.8rem;
  padding-left: 0.5em;
}
.affiliation-info-form-note::before {
  content: '※';
}

.affiliation-info .member-register {
  width: 600px;
  margin: 0 auto;
}

.affiliation-info .row.q-gutter-sm {
  margin-top: 5px;
}

.affiliation-info .row.q-gutter-sm:first-child {
  margin-top: 0;
}
</style>

<style>
.affiliation-info .row.q-gutter-sm .input-area.tel-area {
  width: 220px;
}
</style>
